import React, { useState } from 'react';
import { useCategory } from '@/contexts/CategoryManagementContext';
import { getCategoryForItem, formatDateBR, getItemTypeLabel } from '@/lib/utils';
import { motion } from 'framer-motion';
import { Target, Users, Ticket, Settings, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';

const TimelineView = ({ goals, services, attendance, onEdit }) => {
  const [filterType, setFilterType] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const { categories } = useCategory();

  // Combine and normalize data
  const allItems = [
    ...(goals || []).map(i => ({ ...i, type: 'goals', label: i.name })),
    ...(services || []).map(i => ({ ...i, type: 'services', label: i.name })),
    ...(attendance || []).map(i => ({ ...i, type: 'attendance', label: i.title })),
  ].filter(i => i.dueDate && i.lifecycleStatus === 'Aberto');

  // Sort by date ascending (soonest first)
  allItems.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));

  // Filter
  const filteredItems = allItems.filter(item => {
    const typeMatch = filterType === 'all' || item.type === filterType;
    if (!typeMatch) return false;

    const category = getCategoryForItem(item, categories);
    const catMatch = filterCategory === 'all' || (category && category.id === filterCategory);
    
    return catMatch;
  });

  const getTypeIcon = (type) => {
    switch(type) {
      case 'goals': return <Target className="w-4 h-4" />;
      case 'services': return <Users className="w-4 h-4" />;
      case 'attendance': return <Ticket className="w-4 h-4" />;
      default: return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-wrap gap-4 bg-white/5 p-4 rounded-xl border border-white/10 items-center">
        <div className="flex items-center gap-2 text-gray-400 mr-2">
          <Filter className="w-4 h-4" />
          <span className="text-sm font-medium">Filtrar:</span>
        </div>
        
        <select 
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="bg-black/50 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-orange-500"
        >
          <option value="all">Todos os Tipos</option>
          <option value="goals">Metas</option>
          <option value="services">Serviços</option>
          <option value="attendance">Atendimentos</option>
        </select>

        <select 
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="bg-black/50 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-orange-500"
        >
          <option value="all">Todas as Categorias</option>
          {categories.map(cat => (
            <option key={cat.id} value={cat.id}>{cat.name}</option>
          ))}
        </select>
      </div>

      {/* Timeline */}
      <div className="relative pl-8 border-l-2 border-gray-800 space-y-8 ml-4">
        {filteredItems.length === 0 ? (
          <div className="text-gray-500 italic py-8">Nenhum item encontrado com os filtros selecionados.</div>
        ) : (
          filteredItems.map((item, index) => {
            const category = getCategoryForItem(item, categories);
            
            return (
              <motion.div 
                key={`${item.type}-${item.id}`}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="relative"
              >
                {/* Timeline Dot */}
                <div 
                  className="absolute -left-[41px] top-1 w-5 h-5 rounded-full border-4 border-[#0a0a0a]"
                  style={{ backgroundColor: category ? category.color : '#666' }}
                />

                <div 
                  className="bg-white/5 border border-white/10 rounded-xl p-4 hover:bg-white/10 transition-colors cursor-pointer group"
                  onClick={() => onEdit(item, item.type)}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1">
                          {getTypeIcon(item.type)}
                          {getItemTypeLabel(item.type)}
                        </span>
                        {category && (
                          <span 
                            className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase"
                            style={{ color: category.color, backgroundColor: `${category.color}20` }}
                          >
                            {category.name}
                          </span>
                        )}
                      </div>
                      <h3 className="text-lg font-bold text-white group-hover:text-orange-500 transition-colors">
                        {item.label}
                      </h3>
                      <p className="text-sm text-gray-400 mt-1">
                        Responsável: {item.assignedTo || item.client || 'N/A'}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-black text-white">
                        {new Date(item.dueDate).getDate()}
                      </div>
                      <div className="text-sm text-gray-500 uppercase font-bold">
                        {new Date(item.dueDate).toLocaleString('default', { month: 'short' })}
                      </div>
                      <div className="text-xs text-gray-600 mt-1">
                        {new Date(item.dueDate).getFullYear()}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default TimelineView;